<template>
	<Toast group="guest-ready" position="top-right">
		<template #message="{ message }">
			<div class="flex flex-col gap-2 w-full">
				<div class="font-semibold">{{ message.summary }}</div>
				<div v-if="message.onCall" class="flex gap-2">
					<Button size="small" severity="primary" @click="message.onCall()">{{ t('guest.ringUp') }}</Button>
					<Button size="small" severity="secondary" @click="toast.remove(message)">{{ t('guest.later') }}</Button>
				</div>
			</div>
		</template>
	</Toast>
</template>

<script setup lang="ts">
/**
 * Mounts the guest-ready popup (spec §8): a sticky PrimeVue toast with "Ring op"/"Senere" plus an OS notification whose
 * click brings the app to the front. See utilities/guestReadyToasts for the logic.
 */
import { onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import { useToast } from 'primevue/usetoast'
import type { ToastMessageOptions } from 'primevue/toast'
import Toast from 'primevue/toast'
import Button from 'primevue/button'
import DesktopNotification from '@/utilities/DesktopNotification'
import { useElectronStore } from '@/stores/Electron'
import { useSettingsStore } from '@/stores/Settings'
import { ringUpGuest } from '@/utilities/guestCall'
import { createGuestReadyToasts } from '@/utilities/guestReadyToasts'

const { t } = useI18n()
const toast = useToast()
const electron = useElectronStore()
const ss = useSettingsStore()

/** The sticky toasts we have added, by invite id, so GuestInvite:Left can remove the right one */
/** A toast message that carries its own "Ring op" action */
type GuestReadyMessage = ToastMessageOptions & { onCall?: () => void }

const messages = new Map<string, GuestReadyMessage>()

const controller = createGuestReadyToasts({
	addReadyToast: (inviteId, guestName, onCall) => {
		// No life: the toast stays until the host answers it or the guest leaves
		const message: GuestReadyMessage = { group: 'guest-ready', severity: 'info', summary: t('guest.readyToast', { name: guestName }), onCall }

		messages.set(inviteId, message)
		toast.add(message)
	},
	addLeftToast: guestName => {
		toast.add({ group: 'guest-ready', severity: 'warn', summary: t('guest.leftToast', { name: guestName }), life: 6000 })
	},
	removeToast: inviteId => {
		const message = messages.get(inviteId)

		if (message) {
			toast.remove(message)
			messages.delete(inviteId)
		}
	},
	notify: (_inviteId, guestName, onClick) =>
		DesktopNotification.show(ss.runtime.appName, t('guest.readyToast', { name: guestName }), undefined, true, onClick),
	focusApp: () => {
		if (ss.runtime.isEmbedded) { void electron.show() }
	},
	onRingUp: (inviteId, guestName) => { void ringUpGuest(inviteId, guestName) }
})

onBeforeUnmount(() => { controller.stop() })
</script>
